import React from "react";
import { formatCurrency } from "../utils/formatCurrency";

// The ledger's rejection box and the patient-responsibility line under the charges.
//
// Both render lists the server already split (denial_service.split_adjustment_codes,
// on the claim payload as `denial_codes` and `patient_responsibility_codes`): the
// denial-group adjustments (CO / OA / PI, plus any RARC remark) belong in the box, and
// every PR code belongs on the patient line, whatever the claim's status. Nothing here
// decides which code is which — David's data-refine list caught a DENIED claim whose
// only "reason" was its PR-3 copay, and the fix is one rule on the server, not two.
//
// Styles live in ClaimsLedgerPage.css (.rejection-callout, .neutral,
// .patient-responsibility-line): this is the ledger page's box, extracted so it can
// be rendered on its own.

function freeText(claim) {
  // The last submission's error, falling back to `notes`: claimmd_service appends
  // clearinghouse correspondence there, and on a rejected claim with no submission
  // history that text IS the rejection reason.
  const history = claim.submission_history || [];
  if (history.length > 0) {
    return history[history.length - 1].error || "";
  }
  return claim.notes || "";
}

function amountText(amount) {
  if (amount === null || amount === undefined) return "";
  if (Math.abs(Number(amount)) < 0.01) return "";
  return formatCurrency(amount);
}

// Only what the claim lines do not already say: a code on a line stays off the box, and
// with nothing left the box is not drawn.
export default function RejectionReason({ claim, descriptions = {}, lineCodes = [] }) {
  if (!claim) return null;
  if (claim.claim_status !== "rejected" && claim.claim_status !== "denied") {
    return null;
  }
  const denial = (claim.denial_codes || []).filter(
    (d) => !lineCodes.includes(d.code),
  );
  const text = freeText(claim);
  if (denial.length === 0 && !text) return null;
  const neutral = denial.length === 0;

  return (
    <div
      className={neutral ? "rejection-callout neutral" : "rejection-callout"}
    >
      <strong>Rejection Reason:</strong> {text}
      {denial.length > 0 && (
        <div className="carc-descriptions">
          {denial.map((d) => {
            const amount = amountText(d.amount);
            return (
              <div key={d.code} className="carc-desc-item">
                <span className={d.group ? "code-chip carc" : "code-chip rarc"}>
                  {d.code}
                </span>
                {descriptions[d.code] && <span>{descriptions[d.code]}</span>}
                {amount && <span className="carc-amount">{amount}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
